// src/components/admin/CreateUserModal.js
// Création d'un compte par l'administrateur — Maxula
// AbodyStudio Limited

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { telephoneValide } from '../../utils/phone';
import { FiX, FiCopy, FiCheckCircle, FiRefreshCw, FiEye, FiEyeOff } from 'react-icons/fi';
import API from '../../services/api';

const ROLES = [
  { key: 'user', label: 'Utilisateur', hint: 'Client de la plateforme.' },
  { key: 'merchant', label: 'Marchand', hint: 'Crée une boutique active immédiatement.' },
  { key: 'staff', label: 'Personnel', hint: "Entre au back-office. Rôle et droits à définir ensuite dans Rôles & Permissions." },
  { key: 'admin', label: 'Administrateur', hint: 'Accès complet, sans restriction.' },
];

// Mot de passe lisible et solide : on évite les caractères ambigus (I l 1 O 0)
// parce que ce mot de passe sera dicté ou recopié à la main par l'admin.
const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const arr = new Uint32Array(12);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join('');
};

const empty = {
  firstName: '', lastName: '', email: '', phone: '',
  password: '', role: 'user', businessName: '', legalName: '',
};

const CreateUserModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [created, setCreated] = useState(null);

  if (!open) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) return toast.error('Prénom et nom obligatoires');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error('Email invalide');
    // v1.448.0 — regle partagee (utils/phone.js), Maroc et etranger.
    // Un compte cree depuis l'admin est un compte comme un autre : sans ce
    // controle, la porte de service rouvrait ce qu'on ferme a l'entree.
    if (!telephoneValide(form.phone)) {
      return toast.error("Téléphone requis. Maroc : 0612345678 — étranger : +33 6 12 34 56 78");
    }
    if (form.password.length < 8) return toast.error('Mot de passe : 8 caractères minimum');
    if (form.role === 'merchant' && !form.businessName.trim()) {
      return toast.error('Nom de la boutique obligatoire pour un marchand');
    }
    try {
      setSaving(true);
      const res = await API.post('/admin/users', form);
      const data = res?.data?.data || null;
      // On n'appelle PAS onClose ici : l'écran de confirmation affiche le mot de
      // passe, et c'est la seule fois où il sera lisible. Fermer tout de suite
      // le ferait perdre.
      setCreated(data);
      toast.success(
        `${data?.firstName || ''} ${data?.lastName || ''}`.trim()
          ? `Compte créé : ${data.firstName} ${data.lastName}`
          : 'Compte créé'
      );
      // La liste est rechargée MAINTENANT, pendant que la fenêtre du mot de
      // passe est encore ouverte : à la fermeture, le nouveau compte est déjà
      // en tête de liste, sans temps d'attente.
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Création impossible');
    } finally { setSaving(false); }
  };

  const close = () => { setForm(empty); setCreated(null); setShowPwd(false); onClose(); };

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success('Copié');
  };

  const input = 'w-full border border-gray-300 rounded px-3 py-2';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={close}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 border-b shrink-0">
          <h2 className="text-lg font-bold text-gray-900">
            {created ? 'Compte créé' : 'Créer un utilisateur'}
          </h2>
          <button onClick={close} className="text-gray-400 hover:text-gray-600"><FiX /></button>
        </div>

        {created ? (
          <div className="p-6 overflow-y-auto">
            <div className="flex items-center gap-2 text-green-700 mb-4">
              <FiCheckCircle /> <span className="font-semibold">{created.firstName} {created.lastName}</span>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 mb-4">
              <b>Notez ce mot de passe maintenant.</b> Il ne sera plus jamais
              affiché — la base ne conserve que son empreinte. Aucun email n'est
              envoyé : transmettez-le vous-même.
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2">
                <span className="text-gray-600">Email</span>
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-mono truncate">{created.email}</span>
                  <button onClick={() => copy(created.email)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 shrink-0"><FiCopy size={14} /></button>
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2">
                <span className="text-gray-600">Mot de passe</span>
                <span className="flex items-center gap-2 min-w-0">
                  <span className="font-mono font-semibold truncate">{created.temporaryPassword}</span>
                  <button onClick={() => copy(created.temporaryPassword)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 shrink-0"><FiCopy size={14} /></button>
                </span>
              </div>
              {created.merchant && (
                <div className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2">
                  <span className="text-gray-600">Boutique</span>
                  <span className="font-semibold truncate">{created.merchant.businessName}</span>
                </div>
              )}
            </div>

            {created.role === 'staff' && (
              <p className="text-sm text-gray-600 mt-4">
                Ce compte n'a <b>aucun droit</b> pour l'instant. Attribuez-lui un
                rôle depuis sa fiche, puis ajustez les cases dans
                « Rôles &amp; Permissions ».
              </p>
            )}

            <button onClick={close} className="w-full mt-6 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700">
              Terminé
            </button>
          </div>
        ) : (
          <>
            <div className="p-6 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Prénom *</label>
                  <input value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} className={input} />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Nom *</label>
                  <input value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} className={input} />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Email *</label>
                <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className={input} placeholder="client@email.com" />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Téléphone <span className="text-red-500">*</span></label>
                <input value={form.phone} onChange={(e) => set({ phone: e.target.value })} className={input} placeholder="0612345678 ou +33 6 12 34 56 78" />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Mot de passe *</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => set({ password: e.target.value })}
                      className={`${input} pr-10`}
                      placeholder="8 caractères minimum"
                    />
                    <button type="button" onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
                      {showPwd ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                    </button>
                  </div>
                  <button type="button" onClick={() => { set({ password: generatePassword() }); setShowPwd(true); }}
                    className="px-3 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1">
                    <FiRefreshCw size={14} /> Générer
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Aucun email n'est envoyé : vous transmettrez ce mot de passe vous-même.
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-2">Type de compte</label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {ROLES.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => set({ role: r.key })}
                      className={`text-left px-3 py-2 rounded-lg border text-sm ${
                        form.role === r.key ? 'border-primary-600 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="font-semibold block">{r.label}</span>
                      <span className="text-xs text-gray-500">{r.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              {form.role === 'merchant' && (
                <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Nom de la boutique *</label>
                    <input value={form.businessName} onChange={(e) => set({ businessName: e.target.value })} className={input} />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Raison sociale</label>
                    <input value={form.legalName} onChange={(e) => set({ legalName: e.target.value })} className={input}
                      placeholder="Identique au nom de la boutique si vide" />
                  </div>
                  <p className="text-xs text-gray-500">
                    La boutique est créée <b>active</b> : c'est vous qui la validez en la créant.
                  </p>
                </div>
              )}

              {form.role === 'admin' && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  Un administrateur a <b>tous les droits</b>, sans restriction possible.
                  Pour un accès partiel, choisissez « Personnel ».
                </div>
              )}
            </div>

            <div className="p-6 pt-4 border-t shrink-0">
              <button onClick={submit} disabled={saving}
                className="w-full py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-60">
                {saving ? 'Création...' : 'Créer le compte'}
              </button>
              <p className="text-xs text-gray-400 mt-2 text-center">
                Le compte est créé vérifié et actif : il peut se connecter tout de suite.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateUserModal;
